/**
 * Art lab «Художественная лаборатория» (art §12): `?screen=artlab&lab=keyframe&kf=fight|fight-rest`,
 * `lab=backgrounds&bg=vault|diaper|quatrefoil|gold|fresco|vellum`, `lab=frames&frame=rect|pointedArch|roundArch|trefoil`.
 * URL params: `q=low|med|high|ultra`, `rdebug=<view>`, `freeze=<s>`, `rm=1`, `flash=0`, `ref=1` (key art beside the
 * panel on phones; always on wide screens for kf=fight), `frame=`, `archK=`.
 *
 * Runs on the engine renderer with its own render-on-demand loop; the `debug/artlab` screen (F1) and the standalone
 * page `artlab.html` (src/debug/artlabPage.ts) both call `mountArtLab`. Layout (F3 `fightLayout`) and real creatures (P1) are
 * injected, because `src/engine` may not import `ui` or `creature` at runtime (architecture §1.1).
 */
import type { Backdrop, FrameKind, PanelRect, QualityTier, Viewport } from '../../shared';
import { createEngineRenderer, type EngineRenderer } from '../renderer';
import { createRenderDebug } from '../debug';
import { FightStage, setTabbyPalette, type CreatureSource, type KeyframeId } from './staging';
import { mountHud, type HudRects } from './hud';
import { isTier } from '../quality';
import { orbitCamera } from '../camera';

/** The subset of F3's `fightLayout()` result the lab uses. */
export interface LabLayout { panel: PanelRect; banner?: PanelRect | null; turnOrder?: PanelRect | null; fervor?: PanelRect | null; prayerTiles?: PanelRect[] | null; chronicle?: PanelRect | null; cls?: string }
export type LabLayoutFn = (vp: { w: number; h: number }) => LabLayout;

export interface ArtLabDeps {
  layout?: LabLayoutFn | null;
  creatures?: CreatureSource | null;
  /** URL of the north-star image (default `/docs/concept/keyart-arena.jpg`, served by the dev server). */
  keyArtUrl?: string;
}

export interface ArtLab {
  renderer: EngineRenderer;
  ready: Promise<void>;
  resize(vp: Viewport): void;
  dispose(): void;
}

/** GDD N27 reference rects when no layout function is injected (390 × 844 and 1440 × 900 rows of ui-ux §6.12.1). */
export function fallbackLayout(vp: { w: number; h: number }): LabLayout {
  if (vp.w >= 1024) {
    const s = Math.min(vp.w / 1440, vp.h / 900);
    return { panel: { x: 18 * s, y: 84 * s, w: 1060 * s, h: 662 * s }, cls: 'desk' };
  }
  const w = vp.w - 32;
  const h = Math.round((461 / 844) * vp.h);
  return { panel: { x: 16, y: Math.round((187 / 844) * vp.h), w, h }, cls: 'phone' };
}

export async function mountArtLab(host: HTMLElement, canvas: HTMLCanvasElement, q: URLSearchParams, deps: ArtLabDeps = {}): Promise<ArtLab> {
  const lab = q.get('lab') ?? 'keyframe';
  const kf = (q.get('kf') ?? 'fight') as KeyframeId;
  const qp = q.get('q');
  const quality: QualityTier | 'auto' = isTier(qp) ? qp : 'auto';
  const reduced = q.get('rm') === '1' || q.get('motion') === 'reduced';
  const freezeAt = q.has('freeze') ? Number(q.get('freeze')) : null;
  const renderer = await createEngineRenderer({ canvas, quality, reducedMotion: reduced, flashes: q.get('flash') !== '0', frameBreakCap: 2 });
  const layoutFn = deps.layout ?? fallbackLayout;
  let stage: FightStage | null = null;
  let disposeHud: (() => void) | null = null;
  let vp: Viewport = readViewport();

  const bg = (q.get('bg') ?? 'vault') as Backdrop;
  if (lab === 'triptych') renderer.setScene({ kind: 'arena', arenaId: 'lapis_vault', backdrop: 'vault' });
  if (lab === 'keyframe' || lab === 'frames' || lab === 'materials') renderer.setScene({ kind: 'arena', arenaId: 'lapis_vault', backdrop: lab === 'keyframe' ? 'vault' : bg });
  else if (lab === 'backgrounds') renderer.setScene({ kind: 'stage', backdrop: bg === 'fresco' || bg === 'vellum' ? bg : 'vault' });
  if (lab === 'backgrounds' && bg !== 'vault') renderer.panel.setBackdrop(bg);
  if (q.get('rdebug')) renderer.debug.setView(q.get('rdebug'));
  if (freezeAt !== null) renderer.freeze(freezeAt);

  const place = (): void => {
    vp = readViewport();
    renderer.resize(vp);
    const L = layoutFn({ w: vp.cssW, h: vp.cssH });
    const wide = vp.cssW >= 1024;
    const frame = (q.get('frame') ?? (lab === 'frames' ? 'pointedArch' : 'trefoil')) as FrameKind;
    const archK = q.has('archK') ? Number(q.get('archK')) : wide ? 1.0 : 0.35;
    renderer.panel.set({ rect: L.panel, frame, archK });
    if (lab === 'keyframe') renderer.setEngaged(kf === 'fight' ? 0.58 : null);
    const cam = q.get('cam');
    if (cam) {
      const yaw = cam === 'side' ? 90 : cam === 'back' ? 180 : cam === 'threequarter' ? 35 : cam === 'top' ? 0 : 0;
      renderer.camera.set(orbitCamera([Number(q.get('cx') ?? 0.2), 0.5, 0], Number(q.get('ch') ?? 1.6), 24, cam === 'top' ? 70 : 8, yaw));
    }
    disposeHud?.();
    disposeHud = null;
    if (lab === 'keyframe' && q.get('hud') !== '0') {
      const showRef = wide || q.get('ref') === '1';
      const refRect: PanelRect | null = showRef
        ? wide
          ? { x: L.panel.x + L.panel.w + 16, y: L.panel.y, w: Math.max(120, vp.cssW - (L.panel.x + L.panel.w) - 32), h: L.panel.h }
          : { x: vp.cssW * 0.52, y: 8, w: vp.cssW * 0.46, h: vp.cssW * 0.46 * 1.5 }
        : null;
      const rects: HudRects = {
        panel: L.panel, banner: L.banner ?? null, turnOrder: wide ? null : L.turnOrder ?? null, fervor: L.fervor ?? null, prayerTiles: L.prayerTiles ?? null,
        chronicle: wide ? null : L.chronicle ?? null,
      };
      disposeHud = mountHud(host, rects, { refSrc: showRef ? (deps.keyArtUrl ?? '/docs/concept/keyart-arena.jpg') : null, refRect });
    }
  };

  if (q.get('fur')) setTabbyPalette(q.get('fur')!);
  if (lab === 'keyframe' || lab === 'materials' || lab === 'frames' || lab === 'backgrounds') {
    if (lab === 'keyframe' || lab === 'materials') stage = new FightStage(renderer, lab === 'keyframe' ? kf : 'fight-rest', deps.creatures ?? null);
  }
  if (lab === 'triptych') {
    // art §7.3 triptych mechanics test: 20 / 30 / 50 % bands, band 1 freezes on its shot end
    stage = new FightStage(renderer, 'fight', deps.creatures ?? null);
    stage.update(0);
    const g = 6 / 429;
    renderer.setSubPanels([
      { rect01: [0, 0, 1, 0.2 - g / 2], hold: 'freezeOnEnd' },
      { rect01: [0, 0.2 + g / 2, 1, 0.3 - g], hold: 'freezeOnEnd' },
      { rect01: [0, 0.5 + g / 2, 1, 0.5 - g / 2], hold: 'live' },
    ], { drawDividersMs: 120, dissolveMs: 150 });
    const cat = stage.actors[0], hd = cat.poser.boneOrigin(cat.rig.index.head ?? 0);
    renderer.setSubPanelCamera(0, orbitCamera([hd[0], hd[1] + 0.13, hd[2]], 0.12, 9, 2, 10));
    renderer.setSubPanelCamera(1, orbitCamera([stage.contactPoint[0] - 0.1, stage.contactPoint[1] - 0.1, stage.contactPoint[2]], 0.3, 14, 6, -8));
    setTimeout(() => renderer.endSubPanelShot(0), 300);
    setTimeout(() => renderer.endSubPanelShot(1), 450);
  }
  place();

  // clocks: presentation runs in real time (twinkle, sheen); the fight key frame holds the hit-stop, so its vfx clock
  // stops 140 ms after the contact with the splats hanging in the air.
  const t0 = performance.now();
  let burstDone = false;
  let raf = 0;
  let frames = 0;
  let resolveReady!: () => void;
  const ready = new Promise<void>((res) => { resolveReady = res; });
  const tick = (now: number): void => {
    raf = requestAnimationFrame(tick);
    const present = freezeAt !== null ? freezeAt * 1000 : now - t0;
    if (stage) stage.update(present / 1000);
    let vfx = present;
    if (stage && lab === 'keyframe' && kf === 'fight') {
      if (!burstDone) {
        const at = stage.contactPoint;
        renderer.vfx.burst('splat', [at[0], at[1], at[2] + 0.02], { seed: 7, dir: [0.6, 0.8, 0.1], count: 16, element: 'ink' });
        renderer.vfx.burst('splat', [at[0] - 0.02, at[1] - 0.02, at[2] + 0.04], { seed: 19, dir: [0.7, 0.9, 0], count: 6, element: 'ink' });
        renderer.advanceTo({ presentMs: present, worldMs: present, vfxMs: 0 });
        burstDone = true;
      }
      vfx = 70;
    }
    renderer.advanceTo({ presentMs: present, worldMs: present, vfxMs: vfx });
    if (renderer.isAnimating() || renderer.frameRequested || frames < 3) {
      renderer.frame();
      frames++;
      if (frames === 3) resolveReady();
    }
    const w = window as unknown as { __artlab?: Record<string, unknown> };
    w.__artlab = { ready: frames >= 3, frames, errors: renderer.errors, stats: renderer.stats, extra: renderer.extra, quality: renderer.quality };
  };
  raf = requestAnimationFrame(tick);
  const onResize = (): void => place();
  window.addEventListener('resize', onResize);

  // __marg.render (standalone; F1's debug feature mounts the same namespace in the app)
  const W = window as unknown as { __marg?: Record<string, unknown> };
  W.__marg = W.__marg ?? {};
  W.__marg.render = createRenderDebug(renderer, { creatures: () => stage?.actors.map((a) => a.handle) ?? [], contactPoint: () => stage?.contactPoint ?? [0, 0.6, 0] });

  return {
    renderer, ready,
    resize: () => place(),
    dispose: () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      disposeHud?.();
      stage?.dispose();
      renderer.destroy();
    },
  };
}

function readViewport(): Viewport {
  return { cssW: window.innerWidth, cssH: window.innerHeight, dpr: window.devicePixelRatio || 1, portrait: window.innerHeight >= window.innerWidth, safe: { t: 0, r: 0, b: 0, l: 0 } };
}
